import FormattedMessage from 'I18n/components/FormattedMessage';
import { useCallback } from 'react';
import styled from 'styled-components';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { RadioInput } from 'foundations-components/transitional/components/RadioInput';
import { GenericSkeleton } from '../../../components/GenericSkeleton';
import { TEAM } from '../../getCheckedOptionFromView';
import { useAllTeams } from '../../useAllTeams';
// @ts-expect-error untyped file
import MakeConnectedAPIDropdownProvider from 'customer-data-reference-ui-components/connector/MakeConnectedAPIDropdownProvider';
// @ts-expect-error untyped file
import { USER } from 'reference-resolvers/constants/ReferenceObjectTypes';
// @ts-expect-error untyped file
import UserReferenceResolver from 'reference-resolvers/resolvers/UserReferenceResolver';
import { TASK_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { Select } from 'foundations-components/transitional/components/Select';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const EMPTY_STATE = `--`;
const UserSelect = MakeConnectedAPIDropdownProvider({
  referenceObjectType: USER,
  referenceResolver: UserReferenceResolver
});
const checkUserSharingSupported = view => view.objectTypeId === TASK_TYPE_ID;
export const TeamRadioOptionPresentational = ({
  checkedOption,
  isCurrentValueCorrupted,
  originalView,
  setCheckedOption,
  setViewUpdates,
  teams,
  viewUpdates
}) => {
  const currentDraftView = Object.assign({}, originalView, viewUpdates);

  //When we load up this radio button in an invalid state (such as if the current team is deleted), we want it to show up as unchecked
  const isChecked = checkedOption === TEAM && !isCurrentValueCorrupted;
  const isUserSharingSupported = checkUserSharingSupported(originalView);
  const teamOnView = teams.find(team => team.id === originalView.teamId);
  const teamsUserIsOn = teams.filter(team => team.isUserOnTeam);
  const possibleTeamsToShareThisWith = Array.from(new Set([teamOnView, ...teamsUserIsOn].filter(x => x !== undefined)));
  const onTeamChange = useCallback(e => {
    setCheckedOption(TEAM);
    setViewUpdates({
      private: true,
      teamId: e.target.value ? Number(e.target.value) : undefined,
      permissionDetails: viewUpdates.permissionDetails
    });
    return;
  }, [setCheckedOption, setViewUpdates, viewUpdates.permissionDetails]);
  const onUserChange = useCallback(e => {
    setCheckedOption(TEAM);
    setViewUpdates({
      private: true,
      teamId: viewUpdates.teamId,
      permissionDetails: {
        userIds: e.target.value
      }
    });
  }, [setCheckedOption, viewUpdates, setViewUpdates]);
  const renderUserSelectContent = useCallback(() => {
    const userIds = viewUpdates.permissionDetails ? viewUpdates.permissionDetails.userIds : [];
    if (userIds.length > 0) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "viewsManagement.modals.manageSharing.usersLabelWithCount",
        options: {
          count: userIds.length
        }
      });
    }
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "viewsManagement.modals.manageSharing.usersLabelPlaceholder"
    });
  }, [viewUpdates.permissionDetails]);

  /**
   * If there are no possible teams to assign this to, render a disabled radio button
   */
  if (possibleTeamsToShareThisWith.length === 0 && !isUserSharingSupported) {
    return /*#__PURE__*/_jsxs(RadioInput, {
      disabled: true,
      "data-test-id": `view-sharing-team-checked=${false}`,
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "viewsManagement.modals.manageSharing.myTeam"
      }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "viewsManagement.modals.manageSharing.errors.noPossibleTeamsToShareWith"
      })]
    });
  }

  /*
   * If there is only one team to assign this to, render a button with the team name next to it
   */
  const firstPossibleTeam = possibleTeamsToShareThisWith[0];
  if (possibleTeamsToShareThisWith.length === 1 && !isUserSharingSupported) {
    return /*#__PURE__*/_jsxs(RadioInput, {
      checked: isChecked,
      value: firstPossibleTeam.id,
      onChange: onTeamChange,
      "data-test-id": `view-sharing-team-checked=${isChecked}`,
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "viewsManagement.modals.manageSharing.myTeam"
      }), isChecked && firstPossibleTeam.name, !isChecked && EMPTY_STATE]
    });
  }
  const optionsFormattedForUISelect = possibleTeamsToShareThisWith.map(({
    id,
    name,
    isUserOnTeam
  }) => {
    return {
      value: id,
      text: name,
      help: !isUserOnTeam && /*#__PURE__*/_jsx(FormattedMessage, {
        message: "viewsManagement.modals.manageSharing.notOnThisTeam"
      })
    };
  });

  /** If there is more than one team to choose from, render a select
   */
  return /*#__PURE__*/_jsxs(RadioInput, {
    "data-test-id": `view-sharing-team-checked=${isChecked}`,
    checked: isChecked,
    value: firstPossibleTeam ? firstPossibleTeam.id : undefined
    /**
     * This onChange is different than UISelect's onChange
     *
     * This onChange is triggered when the user clicks on the My Team radio option (if its enabled)
     * At this point, we need to set `teamId` to _something_, so we default to the first possible team
     *
     * After that initial value is set, the user can then click into the input and select a different one if they prefer,
     * (which will be when UISelect.onChange is called)
     * */,
    onChange: onTeamChange,
    children: [isUserSharingSupported ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "viewsManagement.modals.manageSharing.usersOrTeam"
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "viewsManagement.modals.manageSharing.myTeam"
    }), !isChecked && !isUserSharingSupported && EMPTY_STATE, isChecked && /*#__PURE__*/_jsxs("div", {
      children: [isUserSharingSupported && /*#__PURE__*/_jsx(FormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'viewsManagement.modals.manageSharing.usersFieldLabel'
        }),
        marginBlock: "100",
        children: /*#__PURE__*/_jsx(UserSelect, {
          ButtonContent: renderUserSelectContent,
          clearable: true,
          "data-test-id": "manage-sharing-user-select",
          menuWidth: "auto",
          multi: true,
          onChange: onUserChange,
          showTags: false,
          value: viewUpdates.permissionDetails ? viewUpdates.permissionDetails.userIds.map(id => id.toString()) : []
        })
      }), /*#__PURE__*/_jsx(FormControl, {
        label: isUserSharingSupported && /*#__PURE__*/_jsx(FormattedMessage, {
          message: "viewsManagement.modals.manageSharing.teamFieldLabel"
        }),
        marginBlock: "100",
        children: /*#__PURE__*/_jsx(Select, {
          "data-test-id": "view-sharing-editor-team-select",
          minimumSearchCount: 0,
          options: optionsFormattedForUISelect,
          value: currentDraftView.teamId,
          clearable: isUserSharingSupported,
          onChange: onTeamChange,
          placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "viewsManagement.modals.manageSharing.teamPlaceholder"
          })
        })
      })]
    })]
  });
};
const StyledSkeletonRadioInput = styled(GenericSkeleton).withConfig({
  displayName: "TeamRadioOption__StyledSkeletonRadioInput",
  componentId: "gc899p-0"
})(["height:24px;width:100px;"]);
export const TeamRadioOption = props => {
  const {
    loading,
    error,
    data: teams
  } = useAllTeams();
  if (loading) {
    return /*#__PURE__*/_jsx(RadioInput, {
      disabled: true,
      children: /*#__PURE__*/_jsx(StyledSkeletonRadioInput, {})
    });
  }
  if (error) {
    return /*#__PURE__*/_jsx(RadioInput, {
      disabled: true,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "viewsManagement.modals.manageSharing.errors.teamFetchingError"
      })
    });
  }
  const currentTeamId = props.viewUpdates.teamId || props.originalView.teamId;
  const doesCurrentTeamIdEvenExist = Boolean(teams.find(team => team.id === currentTeamId));
  const isCurrentValueCorrupted = currentTeamId !== undefined && !doesCurrentTeamIdEvenExist;
  const valueIsCorruptedWarning = isCurrentValueCorrupted && /*#__PURE__*/_jsx(FormattedMessage, {
    message: "viewsManagement.modals.manageSharing.errors.teamNoLongerExists"
  });
  return /*#__PURE__*/_jsx(FormControl, {
    error: isCurrentValueCorrupted,
    validationMessage: valueIsCorruptedWarning,
    children: /*#__PURE__*/_jsx(TeamRadioOptionPresentational, Object.assign({
      teams: teams,
      isCurrentValueCorrupted: isCurrentValueCorrupted
    }, props))
  });
};